import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import z from 'zod';

export type RegionProvince = { id: number; name: string };
export type RegionOrganizer = {
  id: number;
  name: string;
  logoUrl: Nullable<string>;
};

export type Region = {
  id: number;
  name: string;
  slug: string;
  states?: RegionProvince[];
  organizers?: RegionOrganizer[];
  statesCount?: number;
  organizersCount?: number;
};

export const regionDetailSchema = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  stateIds: z.array(z.number()).optional(),
  organizerIds: z.array(z.number()).optional(),
});

export const listRegions = async () =>
  getGraphQLQueryClient(null).request<WithMeta<Region>>(
    gql`
      query regions {
        data: regions {
          data {
            id
            name
            slug
            statesCount
            organizersCount
            states {
              id
              name
            }
            organizers {
              id
              name
              logoUrl
            }
          }
        }
      }
    `
  );

export const getRegion = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Region>>(
    gql`
      query region($id: Int!) {
        data: region(id: $id) {
          id
          name
          slug
          states {
            id
            name
          }
          organizers {
            id
            name
            logoUrl
          }
        }
      }
    `,
    { id }
  );

export const createRegion = async (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof regionDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Region>>(
    gql`
      mutation createRegion($input: RegionInput!) {
        data: createRegion(input: $input) {
          id
          name
          slug
        }
      }
    `,
    { input }
  );

export const updateRegion = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: z.infer<typeof regionDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Region>>(
    gql`
      mutation updateRegion($id: Int!, $input: RegionInput!) {
        data: updateRegion(id: $id, input: $input) {
          id
          name
          slug
        }
      }
    `,
    { id, input }
  );

export const deleteRegion = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteRegion($id: Int!) {
        data: deleteRegion(id: $id)
      }
    `,
    { id }
  );
